import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withTranslation, useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import Button from 'components/v2/buttons/Button';
import CargomaticGrid from 'components/v2/AgGrid';
import CargomaticModal from 'components/v2/modal';
import Container from 'components/v2/container';
import CustomerDetails from 'components/modules/user/customer-details';
import Pagination from 'components/v2/PaginationV2';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import UserStore from 'stores/UserStore';
import { masterAccountNameRenderer, companyNameRenderer, nameRenderer, nameCompanyNameFormatter, phoneFormatter, dateFormatter, dateTimeFormatter, markupFormatter, tariffFormatter, tariffRenderer, authorizedFormatter, trainedFormatter, } from './formatters';
const caseInsensitiveComparator = (a, b) => {
    return (a || '').localeCompare(b, 'en', { sensitivity: 'base' });
};
const dateComparator = (a, b) => {
    return new Date(a) - new Date(b);
};
const perPageOptions = [25, 50, 100, 200, 500];
const defaultColDef = {
    editable: false,
    filter: 'agTextColumnFilter',
    tooltipValueGetter: (cell) => {
        return cell.value;
    },
};
const PerPage = (props) => {
    const { t } = useTranslation();
    const labelWidth = {
        width: '120px',
    };
    return (React.createElement("div", { className: "flex justify-between my-auto mr-6", style: labelWidth },
        React.createElement("span", { className: "form-label" }, t('PER_PAGE')),
        React.createElement("select", { className: "h-6 form-select", onChange: props.onChange, value: props.selectedValue }, props.options.map((item) => {
            return (React.createElement("option", { key: item, value: item }, item));
        }))));
};
PerPage.propTypes = {
    onChange: PropTypes.func.isRequired,
    selectedValue: PropTypes.number.isRequired,
    options: PropTypes.array.isRequired,
};
class CustomersModule extends Component {
    constructor(props) {
        super(props);
        this.getCustomers = (page) => {
            const filterModel = this.gridAPI.getFilterModel();
            const sortModel = this.gridColumnAPI
                .getColumnState()
                .filter((col) => !!col.sort);
            const params = this.transformFilter(filterModel);
            const options = {
                per_page: this.state.pageSize,
                page: this.state.activePage,
            };
            if (sortModel.length) {
                options.sort = this.transformSort(sortModel);
            }
            UserWebAPIUtils.getCustomers(params, options);
        };
        this.transformFilter = (filterModel) => {
            Object.keys(filterModel).forEach((field) => {
                const filter = filterModel[field];
                if (filter.filterType === 'date') {
                    filter.dateTo = moment(`${filter.type === 'equals' ? filter.dateFrom : filter.dateTo}`)
                        .endOf('day')
                        .local()
                        .utc();
                    filter.dateFrom = moment(filter.dateFrom).local().utc();
                }
            });
            return filterModel;
        };
        this.transformSort = (sortModel) => {
            // Handle any synthetic columns
            const sorts = [];
            sortModel.forEach((item) => {
                if (item.colId === 'first_name/last_name') {
                    sorts.push({ colId: 'first_name', sort: item.sort });
                    sorts.push({ colId: 'last_name', sort: item.sort });
                }
                else if (item.colId === 'company_name/name') {
                    sorts.push({ colId: 'company_name', sort: item.sort });
                    sorts.push({ colId: 'name', sort: item.sort });
                }
                else if (item.colId === 'date_created') {
                    // Sorting on (virtual) date_created isn't working, but _id is time-based and should sort the same.
                    sorts.push({ colId: '_id', sort: item.sort });
                }
                else {
                    sorts.push(item);
                }
            });
            return sorts;
        };
        this.selectPage = (activePage) => {
            this.setState({ activePage }, () => {
                this.getCustomers(activePage);
            });
        };
        this.onGridReady = (params) => {
            this.gridAPI = params.api;
            this.gridColumnAPI = params.columnApi;
            this.gridColumnAPI.autoSizeColumns();
            this.gridAPI.resetRowHeights();
            this.gridAPI.addEventListener('filterChanged', () => {
                this.getCustomers();
            });
            this.gridAPI.addEventListener('sortChanged', () => {
                this.getCustomers();
            });
            // Kick off initial fetch
            console.log(this.gridColumnAPI);
            this.gridColumnAPI.applyColumnState({
                state: [{ colId: 'date_created', sort: 'desc' }],
                defaultState: { sort: null },
            });
            this.getCustomers();
        };
        this.downloadCsv = () => {
            const params = this.gridAPI.getFilterModel();
            const options = {};
            const sortModel = this.gridColumnAPI
                .getColumnState()
                .filter((col) => !!col.sort);
            if (sortModel.length) {
                options.sort = this.transformSort(sortModel);
            }
            UserWebAPIUtils.downloadCustomersCsv(params, options);
        };
        this.getGrid = () => {
            const gridStyle = {
                height: '700px',
                width: '100%',
                fontSize: '15px',
            };
            return (React.createElement("div", null,
                React.createElement(CargomaticGrid, { columnDefs: this.getColumnDefs(), defaultColDef: defaultColDef, gridStyles: gridStyle, rowData: this.state.customers, onGridReady: this.onGridReady })));
        };
        this.handlePerPageChange = (pageSize) => {
            pageSize = parseInt(pageSize, 10);
            this.setState({ pageSize, activePage: 1 }, () => this.getCustomers());
        };
        this.onCustomerClick = (id) => {
            this.setState({
                match: { params: { id } },
                showCustomerModal: true,
            });
        };
        this.onHideCustomerModal = () => {
            this.setState({ showCustomerModal: false });
            // Fetch data again in case they changed something using the modal.
            this.getCustomers();
        };
        this.state = {
            customers: [],
            pages: 1,
            pageSize: 50,
            activePage: 1,
            showCustomerModal: false,
        };
    }
    componentDidMount() {
        UserStore.addChangeListener(this.getStateFromStore.bind(this));
    }
    componentWillUnmount() {
        UserStore.removeChangeListener(this.getStateFromStore.bind(this));
    }
    getColumnDefs() {
        const { t } = this.props;
        return [
            {
                headerName: t('MASTER_ACCOUNT'),
                field: 'master_account.name',
                cellRendererFramework: masterAccountNameRenderer,
                cellRendererParams: { onClick: this.onCustomerClick },
                comparator: caseInsensitiveComparator,
                pinned: 'left',
            },
            {
                headerName: t('COMPANY_NAME'),
                field: 'company_name/name',
                cellRendererFramework: companyNameRenderer,
                valueGetter: nameCompanyNameFormatter,
                comparator: caseInsensitiveComparator,
                pinned: 'left',
            },
            {
                headerName: titleize(t('NAME')),
                field: 'optimized_name',
                cellRendererFramework: nameRenderer,
                cellRendererParams: { onClick: this.onCustomerClick },
                comparator: caseInsensitiveComparator,
                pinned: 'left',
            },
            {
                headerName: t('TYPE'),
                field: '_type',
                pinned: 'left',
            },
            {
                headerName: titleize(t('AUTHORIZED')),
                field: 'authorized',
                valueGetter: authorizedFormatter,
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: [true, false],
                    suppressSelectAll: true,
                }
            },
            {
                headerName: t('TRAINED'),
                field: 'status',
                filter: 'agTextColumnFilter',
                valueGetter: trainedFormatter,
            },
            {
                headerName: t('EMAIL'),
                field: 'email',
                comparator: caseInsensitiveComparator,
            },
            {
                headerName: t('USERNAME'),
                field: 'username',
                comparator: caseInsensitiveComparator,
            },
            {
                headerName: titleize(t('PHONE')),
                field: 'phone',
                valueFormatter: phoneFormatter,
            },
            {
                headerName: t('SALES_REP'),
                field: 'sales_rep',
                comparator: caseInsensitiveComparator,
            },
            {
                headerName: t('MARKUP'),
                field: 'markup',
                valueGetter: markupFormatter,
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: t('TARIFFS'),
                field: 'tariffs',
                valueGetter: tariffFormatter,
                cellRendererFramework: tariffRenderer,
                filter: false, // they are refs to another collection
                comparator: caseInsensitiveComparator,
                cellStyle: { 'white-space': 'normal' },
                autoHeight: true,
            },
            {
                headerName: t('DOT_HASH'),
                field: 'dot_number',
                comparator: caseInsensitiveComparator,
            },
            {
                headerName: t('SIGNUP_DATE'),
                field: 'date_created',
                valueGetter: dateFormatter,
                filter: false,
                comparator: dateComparator,
            },
            {
                headerName: t('LAST_SIGNIN'),
                field: 'last_login_session',
                valueGetter: dateFormatter,
                filter: 'agDateColumnFilter',
                comparator: dateComparator,
                filterParams: {
                    filterOptions: ['inRange', 'equals'],
                    defaultOption: 'equals',
                    inRangeInclusive: true,
                    includeBlanksInEquals: true,
                },
            },
            {
                headerName: t('LAST_SHIPMENT'),
                field: 'last_ship_date',
                valueGetter: dateTimeFormatter,
                filter: 'agDateColumnFilter',
                comparator: dateComparator,
                filterParams: {
                    filterOptions: ['inRange', 'equals'],
                    defaultOption: 'equals',
                    inRangeInclusive: true,
                    includeBlanksInEquals: true,
                },
            },
        ];
    }
    getStateFromStore() {
        const customerList = UserStore.getCustomerList();
        const customers = customerList.items;
        const totalCount = customerList.count;
        this.setState((prevState) => {
            const pages = Math.ceil(totalCount / prevState.pageSize);
            let { activePage } = prevState;
            if (activePage > pages) {
                activePage = pages;
            }
            return { customers, pages, activePage, totalCount };
        });
    }
    renderHeader() {
        const { t } = this.props;
        return (React.createElement("div", { className: 'flex' },
            React.createElement("div", { className: 'flex-1' },
                React.createElement(Pagination, { first: true, last: true, ellipsis: true, total: this.state.totalCount, items: this.state.pages, perPage: this.state.pageSize, page: this.state.activePage, onPageClick: this.selectPage })),
            React.createElement("div", { className: 'px-2 py-2' },
                React.createElement(Button, { color: 'green', onClick: this.downloadCsv }, t('EXPORT_TO_CSV')))));
    }
    render() {
        const { t } = this.props;
        return (React.createElement(Container, null,
            React.createElement("div", { className: "" },
                React.createElement("h2", null,
                    " ",
                    t('CUSTOMERS')),
                this.renderHeader(),
                this.getGrid(),
                this.state.showCustomerModal && (React.createElement(CargomaticModal, { show: true, onHide: this.onHideCustomerModal, title: "Details", body: React.createElement(CustomerDetails, { match: this.state.match, location: { pathname: 'foo' }, history: window.history }), footer: null, location: "top", dialogClassName: "modal-override" })))));
    }
}
export default withTranslation()(CustomersModule);
